import { DialogContent, Icon } from "@material-ui/core";
import moment from "moment/moment";
import React, { Fragment, useState } from "react";
import type Stripe from "stripe";
import styled from "styled-components";

import { IStripeSubscriptionExtended, WorkspacePlanTier } from "common/interfaces";
import stripeProducts from "common/stripeProducts";
import { Divider } from "js/Components/Divider";
import { ToggleSwitch } from "js/Components/ToggleSwitch";
import { pluralize } from "js/core/utilities/utilities";
import { Gap10 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { themeColors } from "js/react/sharedStyles";
import * as StripeUtils from "js/react/views/Billing/dialogs/stripeUtils";
import { defaultText, settingsLabel } from "js/textStyles";

const StyledDialogContent = styled(DialogContent)`
    &&&.MuiDialogContent-root {
        display: flex;
        flex-direction: "column";
        margin: 0;
        padding: 0;
        justify-content: center;
    }
`;

const SectionTitle = styled.div`
    ${settingsLabel}
    color: #333;
    font-weight: 600;
    text-transform: uppercase;
`;

const SectionContent = styled.div`
    ${defaultText}
    color: ${themeColors.mediumGray};
`;

const TotalDue = styled.div`
    ${defaultText}
    color: #333;
    font-weight: 600;
`;

const SwitchBillingContainer = styled.div`
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    align-self: stretch;
    background: #D6EFF9;
    border-radius: 2px;
    ${defaultText}
    color: ${themeColors.ui_blue};
    font-weight: 600;
`;

export interface SummarySectionProps {
    billingInterval: Stripe.Price.Recurring.Interval;
    tier: WorkspacePlanTier;
    seats?: number;
    showBillingSwitch?: boolean;
    onBillingIntervalChange?: (interval: Stripe.Price.Recurring.Interval) => void;
    subscription: IStripeSubscriptionExtended;
}

export const SummarySection: React.FC<SummarySectionProps> = ({
    tier,
    billingInterval: initialBillingInterval,
    seats = 1,
    showBillingSwitch = true,
    onBillingIntervalChange,
    subscription
}) => {
    const [billingInterval, setBillingInterval] = useState<Stripe.Price.Recurring.Interval>(initialBillingInterval);
    const { productName, plans } = stripeProducts[tier];

    const prices = plans.filter(plan => plan.isDefault);
    const trialDaysRemaining = StripeUtils.Subscription.getTrialDaysRemaining(subscription);

    // Create key-value pairs with internal value and amount
    const priceMap = prices.reduce((acc, plan) => {
        if ("interval" in plan) {
            acc[plan.interval] = {
                internalValue: plan.interval,
                amount: plan.amount,
            };
        }
        return acc;
    }, {} as Record<string, { internalValue: string; amount: number }>);

    const newPlan = priceMap[billingInterval];
    const newPlanAmountDollars = (newPlan?.amount ?? 0) / 100;

    // Calculate savings with annual billing
    const monthlyPrice = (priceMap.month?.amount ?? 0) / 100;
    const yearlyAmount = (priceMap.year?.amount ?? 0) / 100;
    const yearlyMonthlyEquivalent = yearlyAmount / 12;

    const savingsPercentage = monthlyPrice > 0
        ? Math.round(((monthlyPrice - yearlyMonthlyEquivalent) / monthlyPrice) * 100)
        : 0;

    let subtotalLineItem: string;
    let dueString: string;
    let prorationLines: React.ReactNode = null;

    const currentItem = subscription?.items?.data?.[0];
    const currentInterval = currentItem?.price?.recurring?.interval;
    const currentPeriodEnd = subscription?.current_period_end;
    const currentPeriodStart = subscription?.current_period_start;

    // This is the per-seat, per-month price for display
    const effectiveMonthlyPrice = billingInterval === "month" ? monthlyPrice : yearlyMonthlyEquivalent;
    const newSeats = seats;

    const prorationCredit = StripeUtils.Subscription.getProrationCredit(subscription, {
        newPlanAmountDollars,
        newSeats,
    });

    const subtotalAmount = newPlanAmountDollars * newSeats;
    const totalBeforeTax = subtotalAmount - prorationCredit;

    if (trialDaysRemaining > 0) {
        // if trial no proration
        subtotalLineItem = `$${newPlanAmountDollars.toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)}`;
        if (billingInterval === "year") {
            subtotalLineItem = `$${(newPlanAmountDollars / 12).toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)} x 12 months`;
        }

        dueString = `Total due on ${moment().add(trialDaysRemaining, "days").format("MMMM Do YYYY")}`;
    } else if (!currentItem || !currentPeriodEnd || !currentPeriodStart || !currentInterval) {
        //no subscription
        dueString = "Total due today";

        subtotalLineItem = `$${newPlanAmountDollars.toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)}`;
        if (billingInterval === "year") {
            subtotalLineItem = `$${(newPlanAmountDollars / 12).toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)} x 12 months`;
        }
    } else {
        //proration logic
        const now = moment();
        const periodEnd = moment.unix(currentPeriodEnd);
        const periodStart = moment.unix(currentPeriodStart);

        const totalMsInPeriod = periodEnd.valueOf() - periodStart.valueOf();
        const remainingMsInPeriod = periodEnd.valueOf() - now.valueOf();

        // Check for invalid states (e.g., expired period)
        if (totalMsInPeriod <= 0 || remainingMsInPeriod <= 0) {
            // Cannot prorate, charge full amount
            dueString = "Total due today";
            subtotalLineItem = `$${newPlanAmountDollars.toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)}`;
            if (billingInterval === "year") {
                subtotalLineItem = `$${(newPlanAmountDollars / 12).toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)} x 12 months`;
            }
        } else {
            subtotalLineItem = `$${newPlanAmountDollars.toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)}`;
            if (billingInterval === "year") {
                subtotalLineItem = `$${(newPlanAmountDollars / 12).toFixed(2)} x ${pluralize(`seat`, newSeats, `seats`)} x 12 months`;
            }

            if (billingInterval === currentInterval) {
                // Total due today is the difference
                dueString = "Prorated amount due today";

                prorationLines = (
                    <>
                        <FlexBox between fillWidth>
                            <SectionContent>Prorated credit for current plan</SectionContent>
                            <SectionContent>-${prorationCredit.toFixed(2)}</SectionContent>
                        </FlexBox>
                    </>
                );
            } else {
                //new billing cycle logic
                dueString = "Amount due today";

                // Add a line item just for the credit
                prorationLines = (
                    <FlexBox between fillWidth>
                        <SectionContent>Prorated credit for current plan</SectionContent>
                        <SectionContent>-${prorationCredit.toFixed(2)}</SectionContent>
                    </FlexBox>
                );
            }
        }
    }

    const handleBillingIntervalChange = (checked: boolean) => {
        const newInterval = checked ? "year" : "month";
        setBillingInterval(newInterval);
        onBillingIntervalChange?.(newInterval);
    };

    return (
        <StyledDialogContent>
            <FlexBox left column fillWidth>
                <SectionTitle>Summary</SectionTitle>
                <Gap10 />
                <FlexBox between fillWidth>
                    <SectionContent>
                        {productName} Plan - {billingInterval === "month" ? "Monthly" : "Annual"} billing
                    </SectionContent>
                    <SectionContent>${effectiveMonthlyPrice.toFixed(2)}/month</SectionContent>
                </FlexBox>
                {showBillingSwitch && (
                    <SwitchBillingContainer>
                        <FlexBox left gap={5} fillWidth>
                            <Icon style={{ fontSize: 18 }}>percent</Icon>
                            <span style={{ flex: 1 }}>Save {savingsPercentage}% with annual billing</span>
                            <ToggleSwitch value={billingInterval === "year"} onChange={handleBillingIntervalChange} />
                        </FlexBox>
                    </SwitchBillingContainer>
                )}
                <Divider color="#EEE" margin={10} />

                <FlexBox between fillWidth>
                    <SectionContent>{subtotalLineItem}</SectionContent>
                    <SectionContent>${subtotalAmount.toFixed(2)}</SectionContent>
                </FlexBox>

                {prorationLines}

                <FlexBox between fillWidth>
                    <SectionContent>Tax</SectionContent>
                    <SectionContent>Calculated separately</SectionContent>
                </FlexBox>

                <Divider color="#EEE" margin={10} />
                <FlexBox left between fillWidth>
                    <TotalDue>{dueString}</TotalDue>
                    <TotalDue>
                        {`$${totalBeforeTax.toFixed(2)} + Applicable tax`}
                    </TotalDue>
                </FlexBox>

                <Fragment></Fragment>
            </FlexBox>
        </StyledDialogContent>
    );
};
